import React, { useEffect, useState } from 'react';
import { TrainFront, ChevronDown } from 'lucide-react';
import { apiClient } from '../../api/client';
import { logger } from '../../lib/logger';

const KIND_TR = { impulse: 'İlk Hareket', radar: 'Radar HAZIRLAN', official: 'Resmi sinyal', prep: 'Erken HAZIRLAN' };
const VERDICT = {
    caught_early: { label: 'ERKEN YAKALANDI', cls: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10' },
    caught_late: { label: 'GEÇ YAKALANDI', cls: 'text-amber-200 border-amber-500/40 bg-amber-500/10' },
    missed: { label: 'KAÇIRILDI', cls: 'text-rose-300 border-rose-500/40 bg-rose-500/10' },
    unknown: { label: 'GEÇMİŞ YOK', cls: 'text-slate-400 border-slate-600 bg-slate-800' },
};
const pct = (v, d = 1) => (v === null || v === undefined ? '—' : `${Number(v) > 0 ? '+' : ''}${Number(v).toFixed(d)}%`);

const Stat = ({ label, value, sub, testId }) => (
    <div className="rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-2 min-w-[110px]" data-testid={testId}>
        <div className="text-[9px] uppercase tracking-wider text-slate-400">{label}</div>
        <div className="text-lg font-mono font-semibold text-slate-50 leading-tight">{value}</div>
        {sub && <div className="text-[10px] text-slate-400">{sub}</div>}
    </div>
);

const Item = ({ t }) => {
    const v = VERDICT[t.verdict] || VERDICT.unknown;
    const dirCls = t.direction === 'long' ? 'text-emerald-300' : 'text-rose-300';
    return (
        <div className="rounded-md border border-slate-700/70 bg-slate-900/40 px-2.5 py-2 text-[11px] space-y-0.5" data-testid={`train-item-${t.symbol}`}>
            <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono font-bold text-slate-50">{t.symbol.replace('USDT', '')}</span>
                <span className={`font-semibold ${dirCls}`}>{t.direction === 'long' ? '▲ LONG' : '▼ SHORT'}</span>
                <span className="font-mono text-slate-200">{pct(t.change_pct_24h)} 24s</span>
                <span className={`ml-auto border rounded-sm px-1.5 py-0.5 text-[9px] font-bold ${v.cls}`}>{v.label}</span>
            </div>
            <div className="text-slate-300 leading-snug">{t.reason_tr}</div>
            {t.flag && (
                <div className="text-[10px] text-slate-400">
                    İşaret: {KIND_TR[t.flag.kind] || t.flag.kind} · {new Date(t.flag.at).toLocaleString('tr-TR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                    {t.telegram?.official_id ? ` · #${t.telegram.official_id}` : t.telegram?.early ? ' · Telegram gitti' : ''}
                </div>
            )}
        </div>
    );
};

/** Tren Raporu: ±%10 koşan coinlerden kaçını sistem önceden işaretledi? */
export default function TrainReportCard({ compact = false }) {
    const [days, setDays] = useState(7);
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(!compact);
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await apiClient.get('/radar/trains', { params: { days } });
                if (!cancelled) setData(data);
            } catch (e) {
                logger.warn('train report failed', e);
            }
        };
        load();
        const id = setInterval(load, 120000);
        return () => { cancelled = true; clearInterval(id); };
    }, [days]);

    const caught = (data?.items || []).filter((t) => t.verdict.startsWith('caught'));
    const missed = (data?.items || []).filter((t) => t.verdict === 'missed');
    const since = data?.tracking_since ? new Date(data.tracking_since).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : null;

    return (
        <div data-testid="train-report" className="border border-slate-700 bg-slate-800 rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-700 flex items-center gap-x-3 gap-y-1 flex-wrap">
                <TrainFront className="h-4 w-4 text-amber-300" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-slate-100 font-semibold">Tren Raporu — treni yakalıyor muyuz?</span>
                <span className="text-[10px] text-slate-400">24 saatte ±%10 koşan her coin bir "tren". Soru: hareket başlamadan önce sistem işaret verdi mi?</span>
                <div className="ml-auto flex items-center gap-1 text-[10px]">
                    {[7, 30].map((d) => (
                        <button key={d} data-testid={`train-days-${d}`} onClick={() => setDays(d)}
                                className={`px-2 py-0.5 rounded border ${days === d ? 'border-amber-400/60 bg-amber-400/10 text-amber-200' : 'border-slate-700 text-slate-400'}`}>
                            {d} gün
                        </button>
                    ))}
                </div>
            </div>
            <div className="p-3 space-y-3">
                {!data ? (
                    <div className="text-xs text-slate-400">Yükleniyor…</div>
                ) : data.trains === 0 ? (
                    <div className="text-xs text-slate-400" data-testid="train-report-empty">
                        Henüz değerlendirilmiş tren yok{data.unknown ? ` (${data.unknown} tren izleme başlamadan önce koştuğu için sayılmadı)` : ''}.
                        {since ? ` İzleme ${since} itibarıyla açık, 5 dk'da bir ${data.snapshots_24h} kayıt alındı.` : ' İzleme başlıyor.'} İlk ±%10 koşan coin burada görünecek.
                    </div>
                ) : (
                    <>
                        <div className="flex gap-2 flex-wrap">
                            <Stat label="Tren" value={data.trains} sub={`ort. hareket ${pct(data.avg_move_pct)}`} testId="train-stat-total" />
                            <Stat label="Yakalanan" value={`%${data.catch_rate_pct ?? 0}`} sub={`${data.caught}/${data.trains}`} testId="train-stat-caught" />
                            <Stat label="Erken yakalanan" value={`%${data.early_rate_pct ?? 0}`} sub={`hareketin ilk yarısında · ${data.caught_early}`} testId="train-stat-early" />
                            <Stat label="İşaret noktası" value={data.avg_flag_share_pct !== null ? `%${data.avg_flag_share_pct}` : '—'} sub="hareketin yüzde kaçında" testId="train-stat-share" />
                            <Stat label="İşaretten sonra" value={pct(data.avg_capture_pct)} sub="ortalama kalan hareket" testId="train-stat-capture" />
                        </div>
                        <div className="flex gap-x-4 gap-y-1 flex-wrap text-[10px] text-slate-300">
                            <span>Yakalayan: {Object.entries(data.by_kind || {}).map(([k, n]) => `${KIND_TR[k] || k} ${n}`).join(' · ') || '—'}</span>
                            {data.missing_hard?.length > 0 && (
                                <span data-testid="train-missing-hard">Kaçırılanlarda en çok eksik kanıt: {data.missing_hard.slice(0, 3).map((m) => `${m.label} (${m.n})`).join(' · ')}</span>
                            )}
                        </div>
                        <button onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid="train-report-toggle"
                                className="flex items-center gap-1 text-[11px] text-slate-200">
                            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
                            {open ? 'Listeyi gizle' : `Listeyi aç (${caught.length} yakalanan · ${missed.length} kaçırılan)`}
                        </button>
                        {open && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                <div className="space-y-1.5" data-testid="train-caught-list">
                                    <div className="text-[10px] uppercase tracking-wider text-emerald-200">Yakalananlar</div>
                                    {caught.length ? caught.map((t) => <Item key={`${t.symbol}-${t.detected_at}`} t={t} />) : <div className="text-[10px] text-slate-500">—</div>}
                                </div>
                                <div className="space-y-1.5" data-testid="train-missed-list">
                                    <div className="text-[10px] uppercase tracking-wider text-rose-200">Kaçırılanlar — neden?</div>
                                    {missed.length ? missed.map((t) => <Item key={`${t.symbol}-${t.detected_at}`} t={t} />) : <div className="text-[10px] text-slate-500">—</div>}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
